'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import type { ComponentProps, RefObject} from 'react';
import { useState } from 'react';

import { ArrowLeft } from '@zoomit/icons';
import { Box, cn, Flex, Link, noop } from '@zoomit/primitives';
import {
  isMobile,
  NoResult,
  SearchCategory,
  SearchHotContents,
  SearchNoResultDescription,
} from '@zoomit/web-kit';
import { Button } from '@zoomit/zoomit-ds';

import { searchArticle } from '../../../apis/shared/articleSearch';
import { searchProduct } from '../../../apis/shared/search';
import { hotContents, resultContents } from '../../../constants/search';
import SearchResult from '../../search/SearchInstanceResults';

type Props = {
  searchQuery: string;
  onClose?: () => void;
  className?: string;
  ref?: RefObject<HTMLDivElement | null>;
  variant?: ComponentProps<typeof SearchResult>['variant'];
};
export const SearchInstanceLite = (props: Props) => {
  const { onClose, searchQuery, ref, className, variant = 'auto' } = props;
  const [selectedCategory, setSelectedCategory] = useState('all');

  const productQuery = useInfiniteQuery(
    ['product', 'search', searchQuery],
    async ({ pageParam = 1 }) => {
      const res = await searchProduct({
        query: searchQuery,
        products: {
          pageNumber: pageParam,
          pageSize: 6,
        },
        keywordCategories: {
          size: 6,
        },
        categories: {
          size: 6,
        },
      });

      return res.data;
    },
    {
      enabled: searchQuery.length !== 0,

      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.products.currentPage;
        return lastPage.products.hasNext ? currentPage + 1 : undefined;
      },
    },
  );

  const articleQuery = useInfiniteQuery(
    ['article', 'search', searchQuery],
    async ({ pageParam = 1 }) => {
      const res = await searchArticle({
        query: searchQuery,
        articlesPageSize: 5,
        articlesPageNumber: pageParam,
      });

      return res.data;
    },
    {
      enabled: searchQuery.length !== 0,
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.articles.currentPage;
        return lastPage.articles.hasNext ? currentPage + 1 : undefined;
      },
    },
  );

  const isLoading = productQuery.isLoading || articleQuery.isLoading;

  const getEachCategoryResultLength = () => {
    const lastProductPageData = productQuery.data?.pages[productQuery.data?.pages.length - 1];

    const counts = {
      categories: lastProductPageData?.categories.length || 0,
      products: lastProductPageData?.products.totalCount || 0,
      keywordCategories: lastProductPageData?.keywordCategories.length || 0,
      articles: articleQuery.data?.pages[0].articles.totalCount || 0,
    };
    const countsOfAll = Object.values(counts).reduce((prev, curr) => prev + curr, 0);

    return { ...counts, all: countsOfAll };
  };

  const isEmpty = !isLoading && Object.values(getEachCategoryResultLength()).every((count) => count === 0);

  const productLastPageData = productQuery.data?.pages?.[productQuery.data?.pages.length - 1];
  const articleLastPageData = articleQuery.data?.pages?.[articleQuery.data?.pages.length - 1];

  const shouldRenderSearchCategory = isMobile && searchQuery.length > 0 && !isLoading;

  return (
    <div className={cn('overflow-hidden', className)} ref={ref}>
      {hotContents && hotContents.length > 0 && searchQuery.length === 0 && (
        <Box spacing={{ mt: 'x6' }}>
          <SearchHotContents onClose={() => onClose?.()} contents={hotContents} />
        </Box>
      )}

      {shouldRenderSearchCategory && (
        <Box spacing={{ pb: 'x2' }}>
          <SearchCategory
            counts={getEachCategoryResultLength()}
            categories={resultContents}
            selectedCategory={selectedCategory}
            handleChangeCategory={setSelectedCategory}
          />
        </Box>
      )}
      {searchQuery.length > 0 && (
        <>
          <div className="no-scrollbar max-h-[calc(100vh-220px)] overflow-y-auto pt-2 md:max-h-[70vh]">
            {isEmpty && (
              <Flex justifyContent="center">
                <NoResult description={SearchNoResultDescription} />
              </Flex>
            )}
            {resultContents.map((contents, idx) => {
              const props = {
                isLoading,
                onClose: onClose || noop,
                key: idx,
                articles: articleLastPageData?.articles?.source || [],
                keywordCategories: productLastPageData?.keywordCategories || [],
                categories: productLastPageData?.categories || [],
                products: productLastPageData?.products?.source || [],
                searchQuery: searchQuery,
                ...contents,
              };

              return isMobile ? (
                (selectedCategory === 'all' || selectedCategory === contents.value) && (
                  <SearchResult variant={variant} {...props} />
                )
              ) : (
                <SearchResult variant={variant} {...props} />
              );
            })}
          </div>
          {!isLoading && searchQuery && !isEmpty && (
            <Link href={`/search/${searchQuery}`}>
              <Button
                className={cn(
                  'fixed bottom-5 left-1/2 !w-[calc(100%-24px)] -translate-x-1/2',
                  'static bottom-0 left-0 !w-[inherit] translate-x-0',
                )}
                spacing={{ mt: 'x6' }}
                variant={isMobile ? 'outline' : 'text'}
                size={isMobile ? 'full width' : 'fit'}
                leftIcon={<ArrowLeft size="sm" className="!fill-primary1" />}
              >
                مشاهده تمامی نتایج
              </Button>
            </Link>
          )}
        </>
      )}
    </div>
  );
};
